import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from "react"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { LocalePrefixMode } from "services/cms-seo/type"

import { normalisePath } from "./helpers/format"

/** From the backend's `Tenant` row: one panel build serves several brands. */
interface BrandSite {
  /** Origin with no trailing slash, or "" while unresolved. */
  origin: string
  /** Host only, for the compact previews. "" when unresolved. */
  host: string
  /** Root-relative when the origin is unknown. */
  url: (locale: string, path: string) => string
  /** Root-relative always, for previews that render the path alone. */
  path: (locale: string, path: string) => string
  /** "as-needed" serves `defaultLocale` from the bare path. */
  localePrefix: LocalePrefixMode
  /** The language this brand treats as primary. */
  defaultLocale: string
  /** True when this locale's URLs carry its code. */
  hasPrefix: (locale: string) => boolean
  resolved: boolean
}

const DEFAULT_PREFIX: LocalePrefixMode = "always"
const DEFAULT_LOCALE = "en"

const hasPrefixFor = (mode: LocalePrefixMode, defaultLocale: string, locale: string): boolean =>
  mode === "always" || locale !== defaultLocale

const buildPath =
  (mode: LocalePrefixMode, defaultLocale: string) =>
  (locale: string, path: string): string => {
    const normalised = normalisePath(path)
    const suffix = normalised === "/" ? "" : normalised
    return hasPrefixFor(mode, defaultLocale, locale) ? `/${locale}${suffix}` : suffix || "/"
  }

const EMPTY: BrandSite = {
  origin: "",
  host: "",
  url: buildPath(DEFAULT_PREFIX, DEFAULT_LOCALE),
  path: buildPath(DEFAULT_PREFIX, DEFAULT_LOCALE),
  localePrefix: DEFAULT_PREFIX,
  defaultLocale: DEFAULT_LOCALE,
  hasPrefix: (locale) => hasPrefixFor(DEFAULT_PREFIX, DEFAULT_LOCALE, locale),
  resolved: false
}

const BrandSiteCtx = createContext<BrandSite>(EMPTY)

export const useBrandSite = (): BrandSite => useContext(BrandSiteCtx)

export const BrandSiteProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [origin, setOrigin] = useState("")
  const [localePrefix, setLocalePrefix] = useState<LocalePrefixMode>(DEFAULT_PREFIX)
  const [defaultLocale, setDefaultLocale] = useState(DEFAULT_LOCALE)
  const [resolved, setResolved] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const config = await cmsSeoService.getSiteUrl()
      if (cancelled) return

      setOrigin((config?.url ?? "").replace(/\/+$/, ""))
      setLocalePrefix(config?.localePrefix ?? DEFAULT_PREFIX)
      setDefaultLocale(config?.defaultLocale || DEFAULT_LOCALE)
      setResolved(true)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<BrandSite>(() => {
    let host = ""
    if (origin) {
      try {
        host = new URL(origin).host
      } catch {
        host = origin.replace(/^https?:\/\//, "")
      }
    }

    const path = buildPath(localePrefix, defaultLocale)

    return {
      origin,
      host,
      resolved,
      localePrefix,
      defaultLocale,
      path,
      hasPrefix: (locale) => hasPrefixFor(localePrefix, defaultLocale, locale),
      url: (locale, target) => `${origin}${path(locale, target)}`
    }
  }, [origin, resolved, localePrefix, defaultLocale])

  return <BrandSiteCtx.Provider value={value}>{children}</BrandSiteCtx.Provider>
}
